import { useEffect, useRef } from 'react';
import './swipeButton.css';

interface AccordanceConfig {
    superLock?: boolean;
    lock?: boolean;
    underReview?: boolean;
    ineligible?: boolean;
}

interface SwipeButtonProps {
    id: string;
    status: 'locked' | 'unlocked' | string;
    handleExam: () => void;
    statusText: string;
    accordanceConfig?: AccordanceConfig;
    textColor?: string
}

const SwipeButton = (props: SwipeButtonProps) => {
    const swipeButtonDivRef = useRef<HTMLDivElement>(null);
    const swipeButtonRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);

    useEffect(() => {
        const swipeDiv = swipeButtonDivRef.current;
        const swipeButton = swipeButtonRef.current;
        if (!swipeDiv || !swipeButton) return;

        const moveStart = (e: MouseEvent | TouchEvent) => {
            e.preventDefault();
            if (props.status !== 'locked') {
                const config = props.accordanceConfig;
                const blocked = config?.superLock || config?.lock || config?.underReview || config?.ineligible;
                if (!blocked) {
                    isDragging.current = true;
                }
            }
        };

        const moving = (e: MouseEvent | TouchEvent) => {
            if (!isDragging.current) return;
            const swipeDiv = swipeButtonDivRef.current!;
            const swipeButton = swipeButtonRef.current!;

            const clientX = e instanceof TouchEvent ? e.touches[0].clientX : e.clientX;
            const parentLeft = swipeDiv.getBoundingClientRect().left;
            const newLeft = clientX - parentLeft - swipeButton.offsetWidth / 2;

            const minLeft = 5;
            const maxLeft = swipeDiv.offsetWidth - swipeButton.offsetWidth - 5;
            const boundedLeft = Math.min(Math.max(newLeft, minLeft), maxLeft);

            swipeButton.style.left = `${boundedLeft}px`;
        };

        const moveEnd = () => {
            if (!isDragging.current) return;
            const swipeDiv = swipeButtonDivRef.current!;
            const swipeButton = swipeButtonRef.current!;

            const buttonLeft = parseFloat(swipeButton.style.left);
            const maxLeft = swipeDiv.offsetWidth - swipeButton.offsetWidth - 5;

            if (buttonLeft >= maxLeft) {
                props.handleExam();
            }

            isDragging.current = false;
            swipeButton.style.left = '5px';
        };

        const resetButton = () => {
            const swipeButton = swipeButtonRef.current;
            if (swipeButton) {
                swipeButton.style.left = '5px';
            }
        };

        swipeButton.addEventListener('mousedown', moveStart as EventListener);
        document.addEventListener('mousemove', moving as EventListener);
        document.addEventListener('mouseup', moveEnd as EventListener);

        swipeButton.addEventListener('touchstart', moveStart as EventListener);
        document.addEventListener('touchmove', moving as EventListener);
        document.addEventListener('touchend', moveEnd as EventListener);
        window.addEventListener('resize', resetButton);

        return () => {
            swipeButton.removeEventListener('mousedown', moveStart as EventListener);
            document.removeEventListener('mousemove', moving as EventListener);
            document.removeEventListener('mouseup', moveEnd as EventListener);

            swipeButton.removeEventListener('touchstart', moveStart as EventListener);
            document.removeEventListener('touchmove', moving as EventListener);
            document.removeEventListener('touchend', moveEnd as EventListener);
            window.removeEventListener('resize', resetButton);
        };
    }, [props.id, props.handleExam, props.status, props.accordanceConfig]);

    useEffect(() => {
        const swipeButton = swipeButtonRef.current;
        if (swipeButton) {
            swipeButton.style.left = '5px';
        }
    }, [props.id]);

    return (
        <div
            style={{
                color : props.textColor
            }}
            id={'swipeButtonDiv' + props.id}
            ref={swipeButtonDivRef}
            className={`swipeButtonDiv ${props.status === 'locked' ? 'swipeButtonDivLock' : ''}`}
        >
            <div
                id={'swipeButton' + props.id}
                ref={swipeButtonRef}
                className={`swipeButton ${props.status === 'locked' ? 'swipeButtonLock' : ''}`}
            >
                <span className={`arrowLogo ${props.status === 'locked' ? 'arrowLogoLock' : ''}`}>
                    &gt;&gt;&gt;
                </span>
            </div>
            <span>
                {props.accordanceConfig?.superLock || props.accordanceConfig?.lock ? (
                    <>Exam is yet to be unlocked!</>
                ) : (
                    <>{props.statusText}</>
                )}
            </span>
        </div>
    );
};

export default SwipeButton;
